import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AngularMaterialModule } from "src/modules/angular-material.module";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslateModule } from "@ngx-translate/core";

import { DeepValuePipe } from "src/pipes/deep-value.pipe";
import { SafeHTMLPipe } from "src/pipes/safe-html.pipe";
import { SuccessDialogComponent } from "./success-dialog/success-dialog.component";
import { DynamicDropdownSearchComponent } from "./dynamic-dropdown-search/dynamic-dropdown-search.component";
import { LoaderComponent } from "./loader/loader.component";
import { NotificationPreviewComponent } from "./notification-preview/notification-preview.component";
import { SkeletonLoaderComponent } from "./skeleton-loader/skeleton-loader.component";
import { CoreUIAngularModule } from "src/modules/coreui-angular.module";
import { DropdownSearchComponent } from "./dropdown-search/dropdown-search.component";
import { ConfirmationDialogComponent } from "src/pages/shared/confirmation-dialog/confirmation-dialog.component";
import { ErrorDialogComponent } from "src/pages/shared/error-dialog/error-dialog.component";
import { SuccessSnackbarComponent } from "../../components/success-snackbar/success-snackbar.component";
import { ReadMoreComponent } from "./read-more/read-more.component";
import { ErrorComponent } from "./error/error.component";
import { TableComponent } from "./table/table.component";
import { SearchableDropdownComponent } from "./searchable-dropdown/searchable-dropdown.component";
import { AnimatingCountDirective } from "src/directives/animated-counter.directive";
import { DetailViewComponent } from "./detail-view/detail-view.component";
import { FormControllerComponent } from "./form-controller/form-controller.component";
import { TimeAgoPipe } from "src/pipes/time-ago.pipe";
import { FormatSizePipe } from "src/pipes/format-size.pipe";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { HelmetComponent } from "./helmet/helmet.component";
import { MatMenuModule } from "@angular/material/menu";
import { ContentCardComponent } from "./content-card/content-card.component";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { RemarkModalComponent } from "./remark-modal/remark-modal.component";
import { BulkThemeModalComponent } from "./bulk-theme-modal/bulk-theme-modal.component";
import { BreadcrumbsComponent } from "src/pages/shared/breadcrumbs/breadcrumbs.component";
import { RouterLink, RouterLinkActive } from "@angular/router";
import { TagsComponent } from "./tags/tags.component";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatPaginatorModule } from "@angular/material/paginator";
import { SwiperSilderComponent } from "./swiper-silder/swiper-silder.component";
import { ArtifactCardComponent } from "./artifact-card/artifact-card.component";
import { ModelCardComponent } from "./model-card/model-card.component";
import { CardSkeletomLoaderComponent } from "./card/card-skeletom-loader/card-skeletom-loader.component";
import { LastUpdatedDaysPipe } from "../../pipes/last-updated-date.pipe";
import { CardChipsComponent } from "./card/card-chips/card-chips.component";
import { CardHeaderImageComponent } from "./card/card-header-image/card-header-image.component";
import { CardActivitiesComponent } from "./card/card-activities/card-activities.component";
import { CardOwnerDetailsComponent } from "./card/card-owner-details/card-owner-details.component";
import { StarRatingComponent } from "./star-rating/star-rating.component";
import { FormatFileSizePipe } from "src/pipes/format-file-size.pipe";
import { ReasonDetailModalComponent } from "./reason-detail-modal/reason-detail-modal.component";
import { AddingItemsFormComponent } from "./adding-items-form/adding-items-form.component";
import { HideShowArtefactsDialogComponent } from "./hide-show-artefacts-dialog/hide-show-artefacts-dialog.component";
import { MatDialogModule } from "@angular/material/dialog";
import { MatRadioModule } from "@angular/material/radio";
import { CheckboxInputComponent } from "./checkbox-input/checkbox-input.component";
import { ManageAccessComponent } from "./manage-access/manage-access.component";
import { AiScoreCardComponent } from "./details/ai-score-card/ai-score-card.component";
import { ProgressBarComponent } from "./details/ai-score-card/progress-bar/progress-bar.component";
import { AiScoreDetailComponent } from "./details/ai-score-detail/ai-score-detail.component";
import { NoRecordFoundComponent } from "./no-record-found/no-record-found.component";
import { ActivityOverviewComponent } from "./details/activity-overview/activity-overview.component";
import { LicenseControlComponent } from "./details/license-control/license-control.component";
import { VersionFileSkeletonLoaderComponent } from "./details/version-control/version-file-skeleton-loader/version-file-skeleton-loader.component";
import { FileIconComponent } from "./unstructured/file-icon/file-icon.component";
import { IsShowPreviewPipe } from "src/pipes/is-show-preview.pipe";
import { FilePreviewSkeletonLoaderComponent } from "./file-preview-skeleton-loader/file-preview-skeleton-loader.component";
import { UnstructuredFilePreviewComponent } from "./unstructured/unstructured-file-preview/unstructured-file-preview.component";
import { UnstructuredFilesComponent } from "./unstructured/unstructured-files/unstructured-files.component";
import { VersionControlComponent } from "./details/version-control/version-control.component";
import { VersionSkeletonLoaderComponent } from "./version-skeleton-loader/version-skeleton-loader.component";
import { CustomTableComponent } from "./custom-table/custom-table.component";
import { TableSkeletonLoaderComponent } from "./table-skeleton-loader/table-skeleton-loader.component";
import { NgxPaginationModule } from "ngx-pagination";
import { ConfirmationDialogActionComponent } from "./confirmation-dialog-action/confirmation-dialog-action.component";
import { AddNomineeComponent } from "./add-nominee/add-nominee.component";
import { AssignDoaComponent } from "./assign-doa/assign-doa.component";
import { AdminFilterComponent } from "src/components/admin-filter/admin-filter.component";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { UseCasesCardComponent } from "./usecase-card/use-cases-card.component";
// import { ConfirmDialogBoxComponent } from './confirm-dialog-box/confirm-dialog-box.component';
// import { SortingDropdownComponent } from "./sorting-dropdown/sorting-dropdown.component";
// import { GetValueFromListPipe } from "src/pipes/get-value-from-list.pipe";
import { CustomLoadingModalComponent } from "./custom-loading-modal/custom-loading-modal.component";
import { SortingDropdownComponent } from "./sorting-dropdown/sorting-dropdown.component";
import { GetValueFromListPipe } from "src/pipes/get-value-from-list.pipe";
import { DefaultValuePipe } from "src/pipes/default-value.pipe";
import { UploadInputComponent } from "./upload-input/upload-input.component";
import { CardHeaderUploadComponent } from "./card/card-header-upload/card-header-upload.component";
import { BulkUploadCardsComponent } from "./bulk-upload-cards/bulk-upload-cards.component";
import { BulkUploadStarRatingComponent } from "./bulk-upload-star-rating/bulk-upload-star-rating.component";
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { ConfirmDialogBoxComponent } from './confirm-dialog-box/confirm-dialog-box.component';
import { ImportErrorsModalComponent } from './import-errors-modal/import-errors-modal.component';
import { DragDropDirective } from "src/directives/drag-drop.directive";
import { ImagePlaceholderDirective } from "src/directives/image-placeholder.directive";
import { TimelineComponent } from './timeline/timeline.component';
import { PdfViewerComponent } from "./pdf-viewer/pdf-viewer.component";
import { SafeResourceUrlPipe } from "src/pipes/safe-resource-url.pipe";

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  declarations: [
    // SortingDropdownComponent,
    CustomLoadingModalComponent,
    SortingDropdownComponent,
    CustomLoadingModalComponent,
    SearchableDropdownComponent,
    UseCasesCardComponent,
    RemarkModalComponent,
    BulkThemeModalComponent,
    BulkThemeModalComponent,
    RemarkModalComponent,
    SuccessDialogComponent,
    DynamicDropdownSearchComponent,
    LoaderComponent,
    NotificationPreviewComponent,
    SkeletonLoaderComponent,
    DropdownSearchComponent,
    ConfirmationDialogComponent,
    ErrorDialogComponent,
    SuccessSnackbarComponent,
    ReadMoreComponent,
    ErrorComponent,
    TableComponent,
    DeepValuePipe,
    SafeHTMLPipe,
    TimeAgoPipe,
    FormatSizePipe,
    AnimatingCountDirective,
    DetailViewComponent,
    FormControllerComponent,
    HelmetComponent,
    ContentCardComponent,
    BreadcrumbsComponent,
    TagsComponent,
    SwiperSilderComponent,
    ArtifactCardComponent,
    ModelCardComponent,
    CardSkeletomLoaderComponent,
    CardChipsComponent,
    CardHeaderImageComponent,
    CardActivitiesComponent,
    CardOwnerDetailsComponent,
    StarRatingComponent,
    ReasonDetailModalComponent,
    AddingItemsFormComponent,
    HideShowArtefactsDialogComponent,
    CheckboxInputComponent,
    ManageAccessComponent,
    AiScoreCardComponent,
    ProgressBarComponent,
    AiScoreDetailComponent,
    NoRecordFoundComponent,
    ActivityOverviewComponent,
    LicenseControlComponent,
    VersionFileSkeletonLoaderComponent,
    VersionSkeletonLoaderComponent,
    FileIconComponent,
    FilePreviewSkeletonLoaderComponent,
    UnstructuredFilePreviewComponent,
    UnstructuredFilesComponent,
    VersionControlComponent,
    CheckboxInputComponent,
    CustomTableComponent,
    TableSkeletonLoaderComponent,
    ConfirmationDialogActionComponent,
    AddNomineeComponent,
    AssignDoaComponent,
    AdminFilterComponent,
    DefaultValuePipe,
    CardHeaderUploadComponent,
    BulkUploadCardsComponent,
    BulkUploadStarRatingComponent,
    ConfirmDialogComponent,
    ConfirmDialogBoxComponent,
    ImportErrorsModalComponent,
    UploadInputComponent,
    DragDropDirective,
    ImagePlaceholderDirective,
    TimelineComponent,
    PdfViewerComponent,
    SafeResourceUrlPipe,
  ],
  imports: [
    CommonModule,
    FormsModule,
    GetValueFromListPipe,
    ReactiveFormsModule,
    AngularMaterialModule,
    CoreUIAngularModule,
    TranslateModule,
    NgSelectModule,
    MatCheckboxModule,
    CKEditorModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    RouterLink,
    RouterLinkActive,
    MatExpansionModule,
    MatPaginatorModule,
    LastUpdatedDaysPipe,
    FormatFileSizePipe,
    MatDialogModule,
    NgSelectModule,
    MatRadioModule,
    IsShowPreviewPipe,
    NgxPaginationModule,
    MatDatepickerModule,
    MatDatepickerModule,
  ],
  exports: [
    CustomLoadingModalComponent,
    TranslateModule,
    MatDialogModule,
    NgSelectModule,
    MatRadioModule,
    SwiperSilderComponent,
    ArtifactCardComponent,
    ModelCardComponent,
    CardSkeletomLoaderComponent,
    SafeHTMLPipe,
    DeepValuePipe,
    TimeAgoPipe,
    FormatSizePipe,
    SearchableDropdownComponent,
    DynamicDropdownSearchComponent,
    LoaderComponent,
    SkeletonLoaderComponent,
    DropdownSearchComponent,
    ConfirmationDialogComponent,
    ErrorDialogComponent,
    SuccessSnackbarComponent,
    ReadMoreComponent,
    ErrorComponent,
    TableComponent,
    AnimatingCountDirective,
    DetailViewComponent,
    FormControllerComponent,
    HelmetComponent,
    ContentCardComponent,
    RemarkModalComponent,
    BulkThemeModalComponent,
    BreadcrumbsComponent,
    TagsComponent,
    BulkThemeModalComponent,
    BreadcrumbsComponent,
    TagsComponent,
    MatExpansionModule,
    MatPaginatorModule,
    LastUpdatedDaysPipe,
    FormatFileSizePipe,
    StarRatingComponent,
    ArtifactCardComponent,
    ModelCardComponent,
    CardSkeletomLoaderComponent,
    CardChipsComponent,
    CardHeaderImageComponent,
    CardActivitiesComponent,
    CardOwnerDetailsComponent,
    UseCasesCardComponent,
    ReasonDetailModalComponent,
    AddingItemsFormComponent,
    HideShowArtefactsDialogComponent,
    CheckboxInputComponent,
    ManageAccessComponent,
    AiScoreCardComponent,
    ProgressBarComponent,
    AiScoreDetailComponent,
    NoRecordFoundComponent,
    ActivityOverviewComponent,
    LicenseControlComponent,
    VersionFileSkeletonLoaderComponent,
    VersionSkeletonLoaderComponent,
    FileIconComponent,
    FilePreviewSkeletonLoaderComponent,
    UnstructuredFilePreviewComponent,
    UnstructuredFilesComponent,
    IsShowPreviewPipe,
    VersionControlComponent,
    CheckboxInputComponent,
    CustomTableComponent,
    TableSkeletonLoaderComponent,
    AssignDoaComponent,
    AdminFilterComponent,
    DefaultValuePipe,
    MatProgressSpinnerModule,
    SortingDropdownComponent,
    CardHeaderUploadComponent,
    BulkUploadCardsComponent,
    MatDatepickerModule,
    ImportErrorsModalComponent,
    UploadInputComponent,
    DragDropDirective,
    ImagePlaceholderDirective,
    TimelineComponent,
    PdfViewerComponent,
    SafeResourceUrlPipe,
  ],
})
export class SharedAppModule {}
